<template>
  <el-dialog
    id="leaderDemographics"
    :title="dialogTitle"
    :visible.sync="showLocal"
    :width="dialogWidth"
    :show-close="!forced"
  >
    <div
      v-if="forced"
      class="subtitle"
    >
      {{ $t('demographics.completeBelowLeader') }}
    </div>
    <div class="content">
      <el-form
        ref="demographicsForm"
        :model="form"
        :rules="rules"
        :label-position="['desktop', 'laptop'].includes($mq) ? 'right' : 'top'"
      >
        <el-form-item
          :label="`1. ${$t('demographics.industries.questionLeader')}`"
          prop="industries"
        >
          <el-select
            v-model="form.industries"
            :value="null"
            multiple
            :loading="processLoadingList.length > 0"
            style="width: 100%;"
            :placeholder="$t('demographics.pleaseSelect')"
          >
            <el-option
              v-for="(industry, iKey) in industriesList"
              :key="`industry${iKey}`"
              :label="industry.value"
              :value="industry.key"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          :label="`2. ${$t('demographics.funcResponsibilities.questionLeader')}`"
          prop="func_responsibilities"
        >
          <el-select
            v-model="form.func_responsibilities"
            :value="null"
            multiple
            :loading="processLoadingList.length > 0"
            style="width: 100%;"
            :placeholder="$t('demographics.pleaseSelect')"
          >
            <el-option
              v-for="(func, iKey) in functionalAreasList"
              :key="`func${iKey}`"
              :label="func.value"
              :value="func.key"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          :label="`3. ${$t('demographics.highestLvlSeniority.questionLeader')}`"
          prop="highest_lvl_seniority"
        >
          <el-select
            v-model="form.highest_lvl_seniority"
            :value="null"
            :loading="processLoadingList.length > 0"
            style="width: 100%;"
            :placeholder="$t('demographics.pleaseSelect')"
          >
            <el-option
              v-for="(lv, iKey) in highestLevelList"
              :key="`lv${iKey}`"
              :label="lv.value"
              :value="lv.key"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          :label="`4. ${$t('demographics.coachingExp.questionLeader')}`"
          prop="corp_years_exp"
        >
          <el-select
            v-model="form.corp_years_exp"
            :value="null"
            :loading="processLoadingList.length > 0"
            style="width: 100%;"
            :placeholder="$t('demographics.pleaseSelect')"
          >
            <el-option
              v-for="(exp, iKey) in experienceList"
              :key="`exp${iKey}`"
              :label="exp.value"
              :value="exp.key"
            />
          </el-select>
        </el-form-item>
      </el-form>
    </div>

    <div class="buttonWrap">
      <div style="width: 100%; padding: 5px 0;">
        <el-button
          type="primary"
          round
          @click="clickSubmit"
        >
          {{ forced ? $t('common.submit') : $t('common.save') }}
        </el-button>
      </div>

      <div style="width: 100%; padding: 5px 0;">
        <el-button
          v-if="forced"
          type="text"
          size="mini"
          style="color: #a1a1a1; text-decoration: underline;"
          @click="clickSkipStep"
        >
          {{ $t('demographics.skipThisStep') }}
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import api from '@/api';
import { mapState, mapMutations } from 'vuex';
import dialog from '@/mixins/dialog';

export default {
  name: 'LeaderDemographics',

  mixins: [
    dialog,
  ],

  props: {
    forced: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      processLoadingList: [],
      processSave: false,
      firstLoaded: true,
      form: {
        industries: null,
        func_responsibilities: null,
        highest_lvl_seniority: null,
        corp_years_exp: null,
      },
      rules: {
        industries: [
          { required: true, message: this.$t('demographics.fieldRequired'), trigger: 'blur' },
        ],
        func_responsibilities: [
          { required: true, message: this.$t('demographics.fieldRequired'), trigger: 'blur' },
        ],
        highest_lvl_seniority: [
          { required: true, message: this.$t('demographics.fieldRequired'), trigger: 'blur' },
        ],
        corp_years_exp: [
          { required: true, message: this.$t('demographics.fieldRequired'), trigger: 'blur' },
        ],
      },
      industriesList: [],
      functionalAreasList: [],
      highestLevelList: [],
      experienceList: [],
    };
  },

  computed: {
    ...mapState({
      user_demographics: (state) => state.user.profile.demographics,
    }),

    dialogTitle() {
      return this.forced ? this.$t('demographics.asStart') : this.$t('demographics.proBackground');
    },
  },

  watch: {
    showLocal: {
      immediate: true,
      handler(newValue) {
        if (newValue && this.firstLoaded) {
          this.getIndustryList();
          this.getFunctionalAreasList();
          this.getHighestLevelList();
          this.getExperienceList();
          this.firstLoaded = false;
        }
      },
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    ...mapMutations('user', [
      'setLeaderDemographics',
    ]),

    async getIndustryList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getIndustryTypes();
        this.industriesList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    async getFunctionalAreasList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getFunctionalAreaTypes();
        this.functionalAreasList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    async getHighestLevelList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getOrgLvlTypes();
        this.highestLevelList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    async getExperienceList() {
      try {
        this.processLoadingList.push('run');
        const response = await api.getCorpWorkExpTypes();
        this.experienceList = response.data;
      } catch (err) {
        console.log(err);
      } finally {
        this.processLoadingList.pop();
      }
    },

    init() {
      if (this.user_demographics) {
        this.form = { ...this.user_demographics };
      }
    },

    async clickSubmit() {
      try {
        this.processSave = true;

        this.$refs.demographicsForm.validate(async (valid) => {
          if (valid) {
            if (this.forced) {
              const response = await api.postLeaderDemographics(this.form);
              this.$emit('closeModal');
              this.setLeaderDemographics(response.data);
            } else {
              const response = await api.patchLeaderDemographics(this.form);
              this.$emit('closeModal');
              this.setLeaderDemographics(response.data);
              this.showLocal = false;
            }
            this.$message.success(this.$t('demographics.savedLeader'));
          }
        });
      } catch (err) {
        console.log(err);
        this.$message.error(err.msg);
      } finally {
        this.processSave = false;
      }
    },

    async clickSkipStep() {
      try {
        this.processSave = true;
        const response = await api.postLeaderDemographics({});
        this.$message.success(this.$t('demographics.savedLeader'));
        this.$emit('closeModal');
        this.setLeaderDemographics(response.data);
      } catch (err) {
        console.log(err);
        this.$message.error(err.msg);
      } finally {
        this.processSave = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  #leaderDemographics {
    .subtitle {
      word-break: break-word;
      padding: 5px 0 15px;
    }

    .buttonWrap {
      display: flex;
      flex-wrap: wrap;
      text-align: center;
      width: 100%;
      justify-content: center;
    }
  }
</style>

<style lang="scss">
  #leaderDemographics {
    .el-dialog__title {
      color: #003865;
      font-weight: bold;
    }

    .el-dialog__body {
      padding: 5px 20px 30px 20px;
    }

    .el-form-item__label {
      word-break: break-word;
      color: black;
    }

    .el-input__inner {
      border-width: 1px;
    }
  }
</style>
